import React, { Children, cloneElement, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { useDocument } from '@nandorojo/swr-firestore';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { CollectionPath } from '@typings/collections.enum';
import { IEquipmentItem } from '@typings/store/equipment.interface';
import { ISkillItem } from '@typings/store/skill.interface';
import useToggle from '@hooks/useToggle';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';
import useMouse from '@react-hook/mouse-position';
import FollowedDescription from '@components/dataDisplay/board/FollowedDescription';

export const Box = styled(motion.div)`
  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

type Props = {
  originItemID?: string;
  data?: IstoreItem | IEquipmentItem | ISkillItem | IinventoryItem | any;
  collectionPath?: string;
  handleClick?: () => void;
  modal?: any; // modal component
  photocardData?: IPhotoCard;
  followTooltip?: boolean;
  hideReinforce?: boolean;
};
function InventoryItem({
  originItemID,
  collectionPath,
  modal,
  data: customData,
  photocardData,
  followTooltip = true,
  hideReinforce = false,
}: Props) {
  // original ref를 받아오는 곳 ---------------------------------------
  const _collectionPath = collectionPath || CollectionPath.STORE;
  const [open, handleToggleOpen, setOpen] = useToggle(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const targetRef = useMouse(ref);
  const [mouseOn, setMouseOn] = useState(false);

  const { data: fetchedOriginData } = originItemID
    ? useDocument<IstoreItem | IEquipmentItem | ISkillItem | any>(
        `${_collectionPath}/${originItemID}`,
        { listen: true },
      )
    : { data: null };
  // 오리지널 item data와 인벤토리 추가속성 data를 합쳐주어야 함
  // customData 값은 undefined 일 수 있음 (? 처리한 이유)
  const mergedMultiple =
    customData?.multiple || (fetchedOriginData as any)?.multiple;

  // originItemID가 있고 실제 원본 데이터도 존재하는 경우
  const _data =
    originItemID && fetchedOriginData
      ? {
          ...customData,
          ...fetchedOriginData,
          id: customData?.id || '',
          isPresent: customData?.isPresent || !!customData?.giftingCharID,
          giftMessage: customData?.giftMessage || '',
          giftingCharID: customData?.giftingCharID || '',
          multiple: mergedMultiple,
        }
      : {
          ...customData,
          title: customData?.customTitle || customData?.title,
          description: customData?.customDescription || customData?.description,
          imgURL: customData?.imgURL || customData?.customImgURL,
          multiple: customData?.multiple,
        };

  if (!_data) return null;

  return (
    <>
      <Box
        ref={ref}
        onClick={handleToggleOpen}
        onMouseEnter={() => setMouseOn(true)}
        onMouseLeave={() => setMouseOn(false)}
      >
        <ItemImgCard data={_data} hideReinforce={hideReinforce} />
      </Box>

      {open && (
        <>
          {/* 넘겨받은 modal 을 표시하고, 해당 컴포넌트에 data와 closeModal 을 넘기는 부분 */}
          {Children.map(modal, (child) =>
            cloneElement(child, {
              data: _data,
              originData: _data,
              closeModal: handleToggleOpen,
              photocardData: photocardData,
            }),
          )}
        </>
      )}
      {followTooltip && (
        <FollowedDescription
          targetRef={targetRef}
          data={_data}
          mouseOn={mouseOn && !open}
        />
      )}
    </>
  );
}

export default InventoryItem;
